import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useFirebase } from 'react-redux-firebase';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import useSiteConfigSelector from 'components/useSiteConfigSelector';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import { sha256 } from 'js-sha256';
import { makeSelectAuth } from 'containers/Auth/selectors';
import useUserId from 'components/Hooks/useUserId';
import Config from 'utils/getEnvConfig';

const { GENERAL_CERTIFICATE_S3_URL } = Config;

export const getCertificateLink = (userId, assessmentId, timestamp) => {
  const filename = `${sha256(`assessments/${assessmentId}`)}${sha256(
    `${userId}-${timestamp}`,
  )}.pdf`;

  return `${GENERAL_CERTIFICATE_S3_URL}${filename}`;
};

const useAssessmentCertificate = answers => {
  const firebase = useFirebase();
  const userId = useUserId();
  const auth = useSelector(makeSelectAuth());
  const takenAssessments = useSelector(
    state => state.assessments.takenAssessments,
  );
  const clientDetails = useSelector(makeSelectClientDetails());
  const [siteConfig] = useSiteConfigSelector(['Features']);

  const isEnabled =
    _get(siteConfig, 'config.assessmentsCertificate.enabled') &&
    (_isEmpty(
      _get(siteConfig, 'config.assessmentsCertificate.clientsInclude', []),
    ) ||
      _get(
        siteConfig,
        'config.assessmentsCertificate.clientsInclude',
        [],
      ).includes(_get(clientDetails, 'shortName'))) &&
    !_get(
      siteConfig,
      'config.assessmentsCertificate.clientsExclude',
      [],
    ).includes(_get(clientDetails, 'shortName'));

  const isAuthenticated = auth.isLoaded && !auth.isEmpty;

  const [assessmentData] = takenAssessments
    .filter(
      item => item.assessment.assessmentID === answers.assessment.assessmentID,
    )
    .sort((a, b) => b.timestamp - a.timestamp);

  const [data, setData] = useState({
    processing: _get(assessmentData, 'certificateProcessing', isAuthenticated),
    available: _get(assessmentData, 'certificateAvailable', false),
    completedAt: _get(assessmentData, 'timestamp'),
  });

  useEffect(() => {
    if (isEnabled && isAuthenticated && !data.processing && !data.available) {
      setData({
        processing: true,
      });
    }
  }, [isAuthenticated, isEnabled, data]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const docId = _get(assessmentData, 'docId');
    if (isEnabled && isAuthenticated && !data.certificateAvailable) {
      const unsubscribe = firebase
        .firestore()
        .collection('user_resources')
        .doc(userId)
        .collection('taken_assessments')
        .doc(docId)
        .onSnapshot(snapshot => {
          const latestData = snapshot.data();
          if (
            !data.available &&
            latestData.certificateProcessing !== _get(data, 'processing')
          ) {
            if (latestData.certificateAvailable) {
              setData({
                processing: _get(latestData, 'certificateProcessing'),
                available: _get(latestData, 'certificateAvailable'),
                completedAt: _get(latestData, 'timestamp'),
              });
            } else if (
              !_isNil(_get(latestData, 'certificateProcessing')) &&
              data.processing !==
                (_get(latestData, 'certificateProcessing') ||
                  _get(latestData, 'context.moved'))
            ) {
              setData({
                processing:
                  _get(latestData, 'certificateProcessing') ||
                  _get(latestData, 'context.moved'),
              });
            }
          }
        });
      return () => unsubscribe();
    }
  }, [isEnabled, isAuthenticated, firebase, assessmentData, userId, data]);

  if (!isEnabled || !isAuthenticated) return [isEnabled, false, null];

  if (_get(data, 'available')) {
    const certificateLink = getCertificateLink(
      userId,
      answers.assessment.assessmentID,
      data.completedAt,
    );
    return [isEnabled, false, certificateLink];
  }

  return [isEnabled, _get(data, 'processing'), null];
};

export default useAssessmentCertificate;
